import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Music, Trash2, ChevronRight, FileAudio, FileText, Clock, Upload, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from '@/lib/utils';
import { readImportFile, importSongs } from '@/lib/exportImport';
import { toast } from 'sonner';
import AboutDrawer from '@/components/AboutDrawer';

const QUOTES = [
    { text: "The best songs come uninvited. You just have to be ready to catch them.", author: "Tom Waits" },
    { text: "A song should make you feel something, not prove something.", author: "Bob Dylan" },
    { text: "The song knows more than you do. Your job is to listen.", author: "Leonard Cohen" },
    { text: "If it sounds good, it is good.", author: "Duke Ellington" },
    { text: "You don't write because you want to say something, you write because you have something to say.", author: "F. Scott Fitzgerald" },
];

export default function SongListPage() {
    const { songs, loading, createSong, deleteSong, addFragment } = useSongs();
    const navigate = useNavigate();
    const [songToDelete, setSongToDelete] = useState(null);
    const [buttonAnimating, setButtonAnimating] = useState(false);
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef(null);
    
    // Get a random quote based on the day
    const todayQuote = QUOTES[new Date().getDate() % QUOTES.length];

    const handleCreateSong = async () => {
        setButtonAnimating(true);
        setTimeout(() => setButtonAnimating(false), 300);
        const newSong = await createSong();
        navigate(`/song/${newSong.id}`);
    };

    const handleDeleteSong = async () => {
        if (songToDelete) {
            await deleteSong(songToDelete.id);
            setSongToDelete(null);
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        try {
            const data = await readImportFile(file);
            const importedIds = await importSongs(data, addFragment, createSong);
            
            if (importedIds.length > 0) {
                toast.success(`Imported ${importedIds.length} song${importedIds.length !== 1 ? 's' : ''}`);
                // Navigate to the first imported song
                navigate(`/song/${importedIds[0]}`);
            }
        } catch (error) {
            console.error('Import error:', error);
            toast.error(error.message || 'Failed to import file');
        } finally {
            setImporting(false);
            if (importInputRef.current) {
                importInputRef.current.value = '';
            }
        }
    };

    const getSongStats = (song) => {
        const audioCount = song.fragments.filter(f => f.type === 'audio').length;
        const textCount = song.fragments.filter(f => f.type === 'text').length;
        return { audioCount, textCount };
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
                                <Music className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-display text-foreground">
                                    Songwriter&apos;s Toolbox
                                </h1>
                                <p className="text-xs text-muted-foreground/70 hidden sm:block">
                                    Local-only · Nothing leaves your device
                                </p>
                            </div>
                        </div>
                        <AboutDrawer />
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
                {/* Create Song Button - Enhanced with animation */}
                <div className="flex gap-3 mb-8">
                    <Button
                        onClick={handleCreateSong}
                        className={`flex-1 h-14 text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all duration-normal ${
                            buttonAnimating ? 'animate-pop' : ''
                        }`}
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Start a New Song
                    </Button>
                    
                    {/* Import Song Button */}
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".json,.stb.json"
                        onChange={handleImportFile}
                        className="hidden"
                    />
                    <Button
                        onClick={() => importInputRef.current?.click()}
                        variant="outline"
                        disabled={importing}
                        className="h-14 px-4 border-dashed hover:border-primary/50"
                        title="Import from .stb.json file"
                    >
                        {importing ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Upload className="w-5 h-5" />
                        )}
                    </Button>
                </div>

                {/* Song List */}
                <div className="space-y-3">
                    {loading ? (
                        // Loading skeletons
                        [...Array(3)].map((_, i) => (
                            <Card key={i} className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-2 flex-1">
                                        <Skeleton className="h-5 w-48" />
                                        <Skeleton className="h-4 w-32" />
                                    </div>
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                </div>
                            </Card>
                        ))
                    ) : songs.length === 0 ? (
                        // Empty state
                        <div className="text-center py-16 animate-fade-in">
                            <div className="w-20 h-20 rounded-full bg-muted mx-auto mb-6 flex items-center justify-center">
                                <Music className="w-10 h-10 text-muted-foreground" />
                            </div>
                            <h2 className="text-xl font-display text-foreground mb-3">
                                No songs yet
                            </h2>
                            <p className="text-muted-foreground max-w-sm mx-auto mb-6 leading-relaxed italic">
                                &quot;{todayQuote.text}&quot;
                            </p>
                            <p className="text-sm text-muted-foreground mb-8">
                                — {todayQuote.author}
                            </p>
                            <Button
                                onClick={handleCreateSong}
                                variant="outline"
                                className="border-primary/30 text-primary hover:bg-primary/5 hover:scale-105 transition-transform"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Start with an idea
                            </Button>
                            <p className="text-xs text-muted-foreground/50 mt-6">
                                Everything stays on this device. No account needed.
                            </p>
                        </div>
                    ) : (
                        // Song cards
                        <div className="stagger-children">
                            {songs.map((song) => {
                                const stats = getSongStats(song);
                                return (
                                    <Card
                                        key={song.id}
                                        className="group relative overflow-hidden border border-border/50 hover:border-border hover:shadow-md transition-all duration-normal cursor-pointer mb-3"
                                        onClick={() => navigate(`/song/${song.id}`)}
                                    >
                                        <div className="p-4 pr-12">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-display text-lg text-foreground truncate">
                                                        {song.title || 'Untitled Song'}
                                                    </h3>
                                                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3.5 h-3.5" />
                                                            {formatDistanceToNow(new Date(song.updatedAt))}
                                                        </span>
                                                        {stats.audioCount > 0 && (
                                                            <span className="flex items-center gap-1">
                                                                <FileAudio className="w-3.5 h-3.5 text-fragment-audio" />
                                                                {stats.audioCount}
                                                            </span>
                                                        )}
                                                        {stats.textCount > 0 && (
                                                            <span className="flex items-center gap-1">
                                                                <FileText className="w-3.5 h-3.5 text-fragment-text" />
                                                                {stats.textCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Delete button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSongToDelete(song);
                                            }}
                                            className="absolute top-1/2 right-3 -translate-y-1/2 p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all duration-fast"
                                            aria-label="Delete song"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>

                                        {/* Chevron indicator */}
                                        <div className="absolute top-1/2 right-10 -translate-y-1/2 opacity-0 group-hover:opacity-50 transition-opacity duration-fast">
                                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer quote */}
                {songs.length > 0 && (
                    <div className="text-center mt-12 px-4 animate-fade-in">
                        <p className="text-sm text-muted-foreground italic">
                            &quot;{todayQuote.text}&quot;
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            — {todayQuote.author}
                        </p>
                    </div>
                )}
            </main>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!songToDelete} onOpenChange={() => setSongToDelete(null)}>
                <AlertDialogContent className="max-w-md animate-scale-in">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-xl">
                            Delete this song?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            &quot;{songToDelete?.title || 'Untitled Song'}&quot; will be removed from your device.
                            This includes all recordings and notes. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep it</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteSong}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}